/**
 * Reports catalog — the single source of truth for every report in
 * the system (M3X.REPORTS §3.1).
 *
 * Each entry is a thin manifest the UI consumes:
 *   - <ReportsLauncherDrawer> reads it to render the Operations-side
 *     contextual launcher
 *   - <ReportsDomainIndex> reads it to render the per-domain landing
 *     page (e.g. /reports/leaves)
 *   - <RelatedReports> picks from it per page context
 *   - PR 3.5 menu registry hydrates from it
 *
 * NOT a runtime config — the actual ReportConfig (columns / filters /
 * charts / endpoint shape) lives next to its page file. This catalog
 * just answers "what reports exist, in what domain, with what
 * description, owned by which agent."
 *
 * Per the audit (PR 1), per-domain PRs (4-13) extend this file.
 */

export type ReportDomain =
  | "dashboards"
  | "leaves"
  | "payroll"
  | "loans"
  | "excuses"
  | "attendance"
  | "eos"
  | "travel"
  | "comp"
  | "benefits"
  | "employee"
  | "org"
  | "analytics"
  | "compliance"
  | "agents";

export type ReportCategory =
  | "operational"
  | "compliance"
  | "cost"
  | "trend"
  | "analytics";

export type ReportAgent =
  | "tashghil"
  | "rawatib"
  | "mutabaa"
  | "khidmat";

export type AIInsightTier = "sonnet" | "haiku" | "template";

export type ExpectedDuration = "fast" | "medium" | "slow" | "background";

export interface ReportManifest {
  /** Stable URL slug — `{id}` segment of `/reports/{domain}/{id}`. */
  id: string;
  /** Domain bucket. Drives the Rail 2 sub-domain page. */
  domain: ReportDomain;
  /** Display title — i18n key resolves at render. Use the
   * literal-EN form here for fallback purposes. */
  titleKey: string;
  /** Display description — i18n key. */
  descriptionKey: string;
  /** Owning agent — drives the AI Brief panel attribution. */
  agent: ReportAgent;
  /** AI insight tier per §1.F. */
  aiTier: AIInsightTier;
  /** Category chip on the launcher / index. */
  category: ReportCategory;
  /** Performance tier per §0.3. Drives the progress indicator. */
  expectedDuration: ExpectedDuration;
  /** Optional KSA Labor Law article citation for compliance reports. */
  article?: string;
  /** Optional: shipped + wired. Defaults to false (placeholder). */
  shipped?: boolean;
}

// ---------------------------------------------------------------------------
// Catalog seed — extends per PR. PR 2 ships:
//   - 4 M21 dashboards (already shipped; just registers them here)
//   - 5 implemented M3X.TRAVEL sections (already shipped)
//
// PR 4+ adds the 157-report set.
// ---------------------------------------------------------------------------

export const REPORTS_CATALOG: ReportManifest[] = [
  // ---- Dashboards (M21 — shipped) ----
  {
    id: "headcount",
    domain: "dashboards",
    titleKey: "reports.dashboard_headcount_title",
    descriptionKey: "reports.dashboard_headcount_desc",
    agent: "tashghil",
    aiTier: "sonnet",
    category: "operational",
    expectedDuration: "fast",
    shipped: true,
  },
  {
    id: "turnover",
    domain: "dashboards",
    titleKey: "reports.dashboard_turnover_title",
    descriptionKey: "reports.dashboard_turnover_desc",
    agent: "tashghil",
    aiTier: "sonnet",
    category: "trend",
    expectedDuration: "fast",
    shipped: true,
  },
  {
    id: "compensation",
    domain: "dashboards",
    titleKey: "reports.dashboard_compensation_title",
    descriptionKey: "reports.dashboard_compensation_desc",
    agent: "rawatib",
    aiTier: "sonnet",
    category: "cost",
    expectedDuration: "fast",
    shipped: true,
  },
  {
    id: "compliance",
    domain: "dashboards",
    titleKey: "reports.dashboard_compliance_title",
    descriptionKey: "reports.dashboard_compliance_desc",
    agent: "mutabaa",
    aiTier: "sonnet",
    category: "compliance",
    expectedDuration: "fast",
    shipped: true,
  },

  // ---- Travel (5 real + 3 to-upgrade; PR 8 adds detail) ----
  {
    id: "active_requests_snapshot",
    domain: "travel",
    titleKey: "reports.travel_active_title",
    descriptionKey: "reports.travel_active_desc",
    agent: "tashghil",
    aiTier: "haiku",
    category: "operational",
    expectedDuration: "fast",
    shipped: true,
  },
  {
    id: "reconciliation_aging",
    domain: "travel",
    titleKey: "reports.travel_recon_title",
    descriptionKey: "reports.travel_recon_desc",
    agent: "tashghil",
    aiTier: "haiku",
    category: "operational",
    expectedDuration: "fast",
    shipped: true,
  },
  {
    id: "per_diem_variance",
    domain: "travel",
    titleKey: "reports.travel_per_diem_title",
    descriptionKey: "reports.travel_per_diem_desc",
    agent: "tashghil",
    aiTier: "haiku",
    category: "cost",
    expectedDuration: "fast",
    shipped: true,
  },
  {
    id: "receipt_over_cap",
    domain: "travel",
    titleKey: "reports.travel_receipt_title",
    descriptionKey: "reports.travel_receipt_desc",
    agent: "mutabaa",
    aiTier: "haiku",
    category: "compliance",
    expectedDuration: "fast",
    shipped: true,
  },
];

// Extend with the full 152 per-domain / cross-domain / compliance /
// agent manifests from manifests.ts (PR 4-13 of M3X.REPORTS).
import { ALL_EXTRA_MANIFESTS } from "./manifests";
REPORTS_CATALOG.push(...ALL_EXTRA_MANIFESTS);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** All reports for a domain (Rail 2 sub-page consumer). */
export function reportsForDomain(domain: ReportDomain): ReportManifest[] {
  return REPORTS_CATALOG.filter((r) => r.domain === domain);
}

/** All categories present in a domain — drives the filter chips. */
export function categoriesForDomain(domain: ReportDomain): ReportCategory[] {
  const set = new Set<ReportCategory>();
  for (const r of reportsForDomain(domain)) set.add(r.category);
  return Array.from(set);
}

/** Look up a single report. */
export function reportById(id: string): ReportManifest | null {
  return REPORTS_CATALOG.find((r) => r.id === id) ?? null;
}

/** The 19 Rail 2 entries per §0.8 (domains + 4 tools). */
export const REPORT_RAIL2_DOMAINS: ReportDomain[] = [
  "dashboards",
  "leaves",
  "payroll",
  "loans",
  "excuses",
  "attendance",
  "eos",
  "travel",
  "comp",
  "benefits",
  "employee",
  "org",
  "analytics",
  "compliance",
  "agents",
];

/** Domain → canonical URL prefix. */
export function domainUrl(domain: ReportDomain): string {
  return `/reports/${domain}`;
}

/** Domain + id → canonical report URL. */
export function reportUrl(domain: ReportDomain, id: string): string {
  return `/reports/${domain}/${id}`;
}
